import { API_URL } from "../../../../config";

/**
 * Thin fetch helper for the Batch 1D+ payment endpoints. Reuses the same
 * token convention as `attoDeskApi` (localStorage `token` → Bearer).
 *
 * All endpoints are parent-scoped on the backend. This helper never
 * accepts a payer_parent_id from the caller for the parent-portal path —
 * the backend derives it from Auth::user()->parent_id.
 *
 * Errors: extract server-side JSON message when available; otherwise
 * bubble the status text. Never surface raw HTML (that is the signal that
 * the request was misrouted — usually a session/CSRF miss).
 */

function authHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  };
}

async function request(path: string, init: RequestInit = {}): Promise<any> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
  });
  const text = await res.text();
  let body: any;
  try { body = text ? JSON.parse(text) : null; }
  catch { body = text; }

  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || res.statusText || "Request failed";
    // Never throw raw HTML at the UI.
    const clean = typeof msg === "string" && msg.startsWith("<") ? "Request failed" : String(msg);
    throw new Error(clean);
  }
  return body;
}

const parentBase = (parentId: string | number) => `/admin/parents/${parentId}`;
const studentBase = (studentId: string | number) => `/admin/students/${studentId}`;

export const paymentsApi = {
  summary: (parent: string | number) => request(`${parentBase(parent)}/payments-summary`),
  methods: (parent: string | number) => request(`${parentBase(parent)}/payment-methods`),
  addMethod: (parent: string | number, body: any) =>
    request(`${parentBase(parent)}/payment-methods`, { method: "POST", body: JSON.stringify(body) }),
  updateMethod: (parent: string | number, methodId: number, body: any) =>
    request(`${parentBase(parent)}/payment-methods/${methodId}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteMethod: (parent: string | number, methodId: number) =>
    request(`${parentBase(parent)}/payment-methods/${methodId}`, { method: "DELETE" }),
  setDefault: (parent: string | number, methodId: number) =>
    request(`${parentBase(parent)}/payment-methods/${methodId}/default`, { method: "POST" }),

  linkedStudentBilling: (parent: string | number) => request(`${parentBase(parent)}/linked-student-billing`),
  paymentsHistory: (parent: string | number, params: any = {}) => {
    const qs = new URLSearchParams(params as any).toString();
    return request(`${parentBase(parent)}/payments${qs ? `?${qs}` : ""}`);
  },

  autoPayList: (parent: string | number) => request(`${parentBase(parent)}/auto-pay`),

  makeManualPayment: (parent: string | number, body: any) =>
    request(`${parentBase(parent)}/payments/manual`, { method: "POST", body: JSON.stringify(body) }),
  makeSavedCardPayment: (parent: string | number, body: any) =>
    request(`${parentBase(parent)}/payments/saved-card`, { method: "POST", body: JSON.stringify(body) }),

  // Eligible payer discovery — we surface all parents linked to the student.
  eligiblePayersForStudent: (studentId: number) =>
    request(`/admin/students/${studentId}/eligible-payers`),
  openInvoicesForStudent: (studentId: number) =>
    request(`/admin/students/${studentId}/open-invoices`),
  cardsForParent: (parentId: number) =>
    request(`${parentBase(parentId)}/payment-methods`),

  // Auto-pay lifecycle
  autoPayCreate: (studentId: number, body: any) =>
    request(`${studentBase(studentId)}/auto-pay`, { method: "POST", body: JSON.stringify(body) }),
  autoPayPause: (studentId: number, agreementId: number) =>
    request(`${studentBase(studentId)}/auto-pay/${agreementId}/pause`, { method: "POST" }),
  autoPayResume: (studentId: number, agreementId: number) =>
    request(`${studentBase(studentId)}/auto-pay/${agreementId}/resume`, { method: "POST" }),
  autoPayCancel: (studentId: number, agreementId: number, reason: string) =>
    request(`${studentBase(studentId)}/auto-pay/${agreementId}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),
  autoPayChangeCard: (studentId: number, agreementId: number, paymentMethodId: number) =>
    request(`${studentBase(studentId)}/auto-pay/${agreementId}/change-card`, { method: "POST", body: JSON.stringify({ payment_method_id: paymentMethodId }) }),
  autoPayHistory: (agreementId: number) =>
    request(`/admin/auto-pay/${agreementId}/history`),

  // Receipts
  receipt: (paymentId: number) => request(`/admin/payments/${paymentId}/receipt`),

  // Reconciliation
  reconciliationList: (params: any = {}) => {
    const qs = new URLSearchParams(params as any).toString();
    return request(`/admin/payments/reconciliation${qs ? `?${qs}` : ""}`);
  },
  reconciliationResolve: (id: number, action: "resolved" | "ignored", notes?: string) =>
    request(`/admin/payments/reconciliation/${id}/resolve`, { method: "POST", body: JSON.stringify({ action, notes }) }),

  // Reversals — single "Refund / Void" admin action
  reversalEligibility: (paymentId: number) =>
    request(`/admin/payments/${paymentId}/reversal-eligibility`),
  reverse: (paymentId: number, body: { amount: number; reason?: string; idempotency_key?: string }) =>
    request(`/admin/payments/${paymentId}/reverse`, { method: "POST", body: JSON.stringify(body) }),

  // GoCardless bank mandates (Canadian PAD). Feature-flagged server-side —
  // endpoints 503 when GOCARDLESS_ENABLED=false.
  bankMandates: (parent: string | number) =>
    request(`${parentBase(parent)}/bank-mandates`),
  startBankMandateSetup: (parent: string | number) =>
    request(`${parentBase(parent)}/bank-mandates/setup`, { method: "POST", body: "{}" }),
  refreshBankMandate: (parent: string | number, mandateRowId: number) =>
    request(`${parentBase(parent)}/bank-mandates/${mandateRowId}/refresh`, { method: "POST", body: "{}" }),
  payWithBankMandate: (parent: string | number, body: {
    student_id: number;
    mandate_row_id: number;
    amount: number;
    allocations: { invoice_id: number; amount: number }[];
  }) =>
    request(`${parentBase(parent)}/payments/bank-mandate`, { method: "POST", body: JSON.stringify(body) }),
};

/** Parent portal API — identity derived by backend from Auth::user()->parent_id. */
export const parentPortalPay = {
  summary: () => request(`/parent-portal/pay/summary`),
  cards:   () => request(`/parent-portal/pay/cards`),
  addCard: (body: any) => request(`/parent-portal/pay/cards`, { method: "POST", body: JSON.stringify(body) }),
  linkedInvoices: () => request(`/parent-portal/pay/linked-invoices`),
  paySavedCard: (body: any) => request(`/parent-portal/pay/saved-card`, { method: "POST", body: JSON.stringify(body) }),
  history: () => request(`/parent-portal/pay/history`),
  autoPay: () => request(`/parent-portal/pay/auto-pay`),
};
